/*
 * Shift Kit — self-contained section rendered:
 *   1. Embedded inside the Pro Carer Circle Hub (`<section id="shift-kit">`)
 *   2. Standalone on the focused `/shift-kit` PWA launcher page
 *
 * Same content, single source of truth. Behind a `pro_carer` HandoverGate
 * in both cases, so it is only ever seen by authenticated Pro Carer members.
 */
import {
  Wrench,
  ShieldCheck,
  AlertTriangle,
  AlertOctagon,
  ClipboardList,
  HomeIcon,
  Activity,
  MessageSquare,
  HelpCircle,
  Ban,
  FileText,
  PoundSterling,
} from "lucide-react";
import { SECTION_HEADING, DriveLink, MakeACopyNote } from "./HubText";
import InstallShiftKit from "../shared/InstallShiftKit";
import ManualHandlingSafety from "./ManualHandlingSafety";
import { NATIVE_FORM_IDS, nativeFormPath } from "../../data/nativeFormTemplateIds";

const SHIFT_GROUPS = [
  {
    key: "start",
    title: "Start of Shift",
    badge: "🟢 Start",
    forms: [
      {
        id: "arrival-check", icon: HomeIcon, title: "Arrival Check (Home + Client Condition)",
        body: "A 2–3 minute snapshot of the client and the home environment when you arrive. Protects you from “that wasn’t like that before…” situations later.",
        formId: NATIVE_FORM_IDS.ARRIVAL_CHECK,
      },
      {
        id: "daily-care-log", icon: ClipboardList, title: "Daily Care Log",
        body: "Clear, professional record of each shift — care provided, well-being, nutrition, medication support, mobility, behaviour, incidents, handover.",
        formId: NATIVE_FORM_IDS.DAILY_CARE_LOG,
      },
      {
        id: "medication", icon: Activity, title: "Daily Medication Record",
        body: "Record medication given, refused, delayed, missed or PRN. Always follow your agreed care responsibilities and only administer if authorised and trained.",
        formId: NATIVE_FORM_IDS.DAILY_MEDICATION_RECORD,
      },
      {
        id: "handover", icon: MessageSquare, title: "Handover Notes",
        body: "Clear, factual, professional, relevant. A good handover helps the next person walk into the shift informed — not confused.",
        formId: NATIVE_FORM_IDS.HANDOVER_NOTES,
      },
    ],
  },
  {
    key: "during",
    title: "During the Shift",
    badge: "🟡 During",
    forms: [
      {
        id: "food-fluid", icon: Activity, title: "Food & Fluid Chart",
        body: "Track what a client eats and drinks throughout the shift. Especially helpful for clients at risk of dehydration, recovering from illness, living with dementia or losing weight.",
        formId: NATIVE_FORM_IDS.FOOD_FLUID_CHART,
      },
      {
        id: "mood-behaviour", icon: HelpCircle, title: "Mood / Behaviour Notes",
        body: "Record behaviours, mood changes, possible triggers and patterns clearly and calmly. Keep observations objective and specific.",
        formId: NATIVE_FORM_IDS.MOOD_BEHAVIOUR_NOTES,
      },
      {
        id: "mobility", icon: Activity, title: "Mobility & Transfers",
        body: "Record walking support, transfers, equipment use, assistance levels and any mobility concerns or falls risks. Never attempt unsafe transfers alone.",
        formId: NATIVE_FORM_IDS.MOBILITY_TRANSFERS,
      },
    ],
  },
  {
    key: "incident",
    title: "If Something Happens",
    badge: "🔴 Incident",
    forms: [
      {
        id: "incident-report", icon: AlertTriangle, title: "Incident Report",
        body: "Falls, medication errors, accidents, injuries, property damage, near misses, situations that simply “didn’t feel right.” Document factually, calmly, professionally.",
        formId: NATIVE_FORM_IDS.INCIDENT_REPORT,
      },
      {
        id: "near-miss", icon: AlertOctagon, title: "Near Miss Report",
        body: "Prove you recognised risk, acted safely and didn’t ignore warning signs. Often saves you if something happens later.",
        formId: NATIVE_FORM_IDS.NEAR_MISS_REPORT,
      },
      {
        id: "safeguarding", icon: ShieldCheck, title: "Safeguarding Concern",
        body: "Possible neglect, abuse, unsafe living conditions, financial concerns, unexplained injuries or behaviour changes. Stick to facts and observations.",
        formId: NATIVE_FORM_IDS.SAFEGUARDING_CONCERN,
      },
      {
        id: "emergency-escalation", icon: AlertTriangle, title: "Emergency / Escalation Record",
        body: "Sudden illness, falls, 999 calls, urgent safeguarding, GP / district nurse / out-of-hours contact. Creates a clear timeline.",
        formId: NATIVE_FORM_IDS.EMERGENCY_ESCALATION,
      },
    ],
  },
  {
    key: "end",
    title: "End of Shift",
    badge: "🔵 End",
    forms: [
      {
        id: "end-of-shift", icon: ClipboardList, title: "End of Shift Summary",
        body: "Clear overview of the shift before handover or sign-off. Reduces misunderstandings and supports safer, more consistent care.",
        formId: NATIVE_FORM_IDS.END_OF_SHIFT_SUMMARY,
      },
      {
        id: "weekly-summary", icon: ClipboardList, title: "Weekly Summary",
        body: "Brings 7 days of care into one clear snapshot — wellbeing, routines, concerns, patterns. Especially useful for family updates and early-warning signs.",
        formId: NATIVE_FORM_IDS.WEEKLY_SUMMARY,
      },
    ],
  },
  {
    key: "protect",
    title: "Protect Yourself",
    badge: "🟣 Protect",
    forms: [
      {
        id: "care-plan-agreement", icon: FileText, title: "Care Plan / Agreement",
        body: "Sets out care provided, hours, payment, responsibilities, boundaries and notice. Read carefully and keep a signed copy.",
        formId: NATIVE_FORM_IDS.CARE_PLAN_AGREEMENT,
      },
      {
        id: "payment-terms", icon: PoundSterling, title: "Payment Terms",
        body: "Exactly how and when payments should be made, what happens if care hours change, and how cancellations/deposits/balances will be handled.",
        interactive: "https://drive.google.com/file/d/11EkAmLnEJQx6AKsuxOPT-VIgwP37q_U2/view?usp=sharing",
        printable: "https://drive.google.com/file/d/1r4t6CFOP5lzCPY9WNqzHTIo5OjLSjGXw/view?usp=drive_link",
      },
      {
        id: "cancellation", icon: Ban, title: "Cancellation Policy",
        body: "Notice periods, payment responsibilities and what happens if shifts, bookings or live-in placements are cancelled at short notice.",
        formId: NATIVE_FORM_IDS.CANCELLATION_POLICY,
      },
    ],
  },
];

export default function ShiftKitSection() {
  return (
    <section id="shift-kit" data-testid="pro-shift-kit" className="scroll-mt-24">
      <div className="max-w-3xl mb-5">
        <div className="overline text-teal mb-2 flex items-center gap-2">
          <Wrench className="w-4 h-4" strokeWidth={2} /> Shift Kit
        </div>
        <h2 className={SECTION_HEADING}>Your everyday on-shift forms.</h2>
        <p className="mt-1.5 text-ink-muted leading-relaxed">
          These records aren’t just paperwork — they’re your evidence, protection and professionalism, every single shift.
        </p>
      </div>

      {/* Pro-only home-screen install prompt. Auto-hides in standalone mode. */}
      <InstallShiftKit />

      <div className="mt-5 rounded-2xl border border-purple/20 bg-purple/5 p-5" data-testid="shift-kit-copy-notice">
        <MakeACopyNote />
      </div>

      <div className="mt-8 space-y-8">
        {SHIFT_GROUPS.map((group) => (
          <div key={group.key} data-testid={`shift-group-${group.key}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="pill bg-slate-100 text-ink">{group.badge}</span>
              <h3 className="font-display text-xl font-semibold">{group.title}</h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {group.forms.map((f) => {
                const Icon = f.icon;
                return (
                  <article id={f.id} key={f.id} className="card-base p-6 flex flex-col scroll-mt-24 min-w-0" data-testid={`shift-form-${f.id}`}>
                    <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <h4 className="font-display text-base font-semibold leading-snug">{f.title}</h4>
                    <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">{f.body}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {f.formId && (
                        <DriveLink href={nativeFormPath(f.formId)} label="Open in JCC Forms" testId={`shift-${f.id}-native`} />
                      )}
                      {f.interactive && (
                        <DriveLink href={f.interactive} label="Interactive" variant="interactive" testId={`shift-${f.id}-interactive`} />
                      )}
                      {f.printable && (
                        <DriveLink href={f.printable} label="Printable" variant="printable" testId={`shift-${f.id}-printable`} />
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Manual handling safety — Stop & Check + scripts + documentation */}
      <div className="mt-10">
        <ManualHandlingSafety />
      </div>
    </section>
  );
}
